import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';
import toast from 'react-hot-toast';

const ReportHistory = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, today, week, month
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReports, setSelectedReports] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    week: 0,
    month: 0,
    totalSize: '0 MB'
  });

  // Mock data for demonstration
  const mockReports = [
    {
      id: 1,
      student_name: "John Doe",
      student_reg: "STU2023001",
      class_name: "10A",
      template_name: "Default Report Card",
      generated_by: "Admin User",
      generated_at: "2023-12-15T10:30:00Z",
      file_size: "1.2 MB",
      type: "single",
      status: "completed"
    },
    {
      id: 2,
      student_name: "Jane Smith",
      student_reg: "STU2023002",
      class_name: "10A",
      template_name: "Default Report Card",
      generated_by: "Admin User",
      generated_at: "2023-12-15T11:15:00Z",
      file_size: "1.1 MB",
      type: "single",
      status: "completed"
    },
    {
      id: 3,
      class_name: "10A",
      template_name: "Default Report Card",
      generated_by: "Admin User",
      generated_at: "2023-12-14T09:45:00Z",
      file_size: "24.5 MB",
      type: "bulk",
      status: "completed"
    },
    {
      id: 4,
      student_name: "Michael Johnson",
      student_reg: "STU2023003",
      class_name: "10B",
      template_name: "Custom Template",
      generated_by: "Admin User",
      generated_at: "2023-12-13T14:20:00Z",
      file_size: "1.3 MB",
      type: "single",
      status: "completed"
    },
    {
      id: 5,
      class_name: "11A",
      template_name: "Default Report Card",
      generated_by: "Admin User",
      generated_at: "2023-12-12T16:10:00Z",
      file_size: "18.7 MB",
      type: "bulk",
      status: "completed"
    }
  ];

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [filter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      // In a real app, you'd fetch from API
      // const response = await adminAPI.getReportHistory({ filter });
      // setReports(response.data);
      
      // Using mock data for now
      setReports(mockReports);
      
      // Calculate stats
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      
      const todayCount = mockReports.filter(r => 
        r.generated_at.startsWith(todayStr)
      ).length;
      
      const weekCount = mockReports.filter(r => {
        const reportDate = new Date(r.generated_at);
        const daysDiff = (today - reportDate) / (1000 * 60 * 60 * 24);
        return daysDiff <= 7;
      }).length;
      
      const monthCount = mockReports.filter(r => {
        const reportDate = new Date(r.generated_at);
        const daysDiff = (today - reportDate) / (1000 * 60 * 60 * 24);
        return daysDiff <= 30;
      }).length;
      
      setStats({
        total: mockReports.length,
        today: todayCount,
        week: weekCount,
        month: monthCount,
        totalSize: calculateTotalSize(mockReports)
      });
    } catch (error) {
      toast.error('Failed to load report history');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // const response = await adminAPI.getReportStats();
      // setStats(response.data);
    } catch (error) {
      toast.error('Failed to load statistics');
    }
  };

  const calculateTotalSize = (reports) => {
    const totalBytes = reports.reduce((sum, report) => {
      const sizeStr = report.file_size || '0 MB';
      const size = parseFloat(sizeStr);
      const unit = sizeStr.includes('GB') ? 1024 * 1024 * 1024 :
                   sizeStr.includes('MB') ? 1024 * 1024 :
                   sizeStr.includes('KB') ? 1024 : 1;
      return sum + (size * unit);
    }, 0);
    
    if (totalBytes > 1024 * 1024 * 1024) {
      return `${(totalBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    } else if (totalBytes > 1024 * 1024) {
      return `${(totalBytes / (1024 * 1024)).toFixed(2)} MB`;
    } else if (totalBytes > 1024) {
      return `${(totalBytes / 1024).toFixed(2)} KB`;
    } else {
      return `${totalBytes} bytes`;
    }
  };

  const handleDeleteReport = async (id) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      // await adminAPI.deleteReport(id);
      setReports(reports.filter(r => r.id !== id));
      toast.success('Report deleted successfully');
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedReports.length === 0) {
      toast.error('No reports selected');
      return;
    }
    
    if (!window.confirm(`Delete ${selectedReports.length} selected reports?`)) return;
    
    try {
      // await adminAPI.deleteReports(selectedReports);
      setReports(reports.filter(r => !selectedReports.includes(r.id)));
      setSelectedReports([]);
      toast.success('Reports deleted successfully');
    } catch (error) {
      toast.error('Failed to delete reports');
    }
  };

  const handleDownloadReport = (report) => {
    toast.success(`Downloading ${report.type === 'bulk' ? 'class' : 'student'} report...`);
    // In real app, trigger download
  };

  const handleRegenerate = (report) => {
    toast.info('Regenerating report...');
    // In real app, trigger regeneration
  };

  const filteredReports = reports.filter(report => {
    const matchesFilter = filter === 'all' || 
      (filter === 'today' && new Date(report.generated_at).toDateString() === new Date().toDateString()) ||
      (filter === 'week' && (new Date() - new Date(report.generated_at)) <= 7 * 24 * 60 * 60 * 1000) ||
      (filter === 'month' && (new Date() - new Date(report.generated_at)) <= 30 * 24 * 60 * 60 * 1000);
    
    const matchesSearch = !searchTerm || 
      report.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.student_reg?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.class_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.template_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedReports(filteredReports.map(r => r.id));
    } else {
      setSelectedReports([]);
    }
  };

  const handleSelectReport = (id) => {
    if (selectedReports.includes(id)) {
      setSelectedReports(selectedReports.filter(reportId => reportId !== id));
    } else {
      setSelectedReports([...selectedReports, id]);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg mr-4">
              <i className="fas fa-file-pdf text-blue-600 text-xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Reports</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg mr-4">
              <i className="fas fa-calendar-day text-green-600 text-xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-500">Today</p>
              <p className="text-2xl font-bold">{stats.today}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg mr-4">
              <i className="fas fa-calendar-week text-yellow-600 text-xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-500">This Week</p>
              <p className="text-2xl font-bold">{stats.week}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg mr-4">
              <i className="fas fa-database text-purple-600 text-xl"></i>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Size</p>
              <p className="text-2xl font-bold">{stats.totalSize}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
            <div className="flex space-x-3">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
              
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search reports..."
                  className="border border-gray-300 rounded-lg pl-10 pr-4 py-2 w-64"
                />
                <i className="fas fa-search absolute left-3 top-3 text-gray-400"></i>
              </div>
            </div>
            
            <div className="flex space-x-3">
              {selectedReports.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                >
                  <i className="fas fa-trash mr-2"></i>
                  Delete Selected ({selectedReports.length})
                </button>
              )}
              
              <button
                onClick={() => {
                  setFilter('all');
                  setSearchTerm('');
                  setSelectedReports([]);
                }}
                className="btn-secondary px-4 py-2"
              >
                <i className="fas fa-redo mr-2"></i>
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Reports Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="py-3 px-4 text-left">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedReports.length === filteredReports.length && filteredReports.length > 0}
                    className="h-4 w-4 text-primary-600"
                  />
                </th>
                <th className="py-3 px-4 text-left font-medium text-gray-700">Report Type</th>
                <th className="py-3 px-4 text-left font-medium text-gray-700">Details</th>
                <th className="py-3 px-4 text-left font-medium text-gray-700">Template</th>
                <th className="py-3 px-4 text-left font-medium text-gray-700">Generated</th>
                <th className="py-3 px-4 text-left font-medium text-gray-700">Size</th>
                <th className="py-3 px-4 text-left font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredReports.length > 0 ? (
                filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedReports.includes(report.id)}
                        onChange={() => handleSelectReport(report.id)}
                        className="h-4 w-4 text-primary-600"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg mr-3 ${
                          report.type === 'bulk' 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          <i className={`fas fa-${report.type === 'bulk' ? 'users' : 'user-graduate'}`}></i>
                        </div>
                        <div>
                          <span className="font-medium capitalize">{report.type}</span>
                          <div className="text-xs text-gray-500">
                            {report.type === 'bulk' ? 'Class Report' : 'Individual'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {report.type === 'single' ? (
                        <div>
                          <div className="font-medium">{report.student_name}</div>
                          <div className="text-sm text-gray-500">
                            {report.student_reg} • {report.class_name}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="font-medium">{report.class_name} Class</div>
                          <div className="text-sm text-gray-500">Bulk generation</div>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-medium">{report.template_name}</div>
                      <div className="text-sm text-gray-500">by {report.generated_by}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-medium">{formatDate(report.generated_at)}</div>
                      <div className="text-sm text-gray-500">
                        {new Date(report.generated_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-medium">{report.file_size}</div>
                      <div className={`text-xs px-2 py-1 rounded-full inline-block ${
                        report.status === 'completed' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {report.status}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDownloadReport(report)}
                          className="text-primary-600 hover:text-primary-800"
                          title="Download"
                        >
                          <i className="fas fa-download"></i>
                        </button>
                        <button
                          onClick={() => handleRegenerate(report)}
                          className="text-green-600 hover:text-green-800"
                          title="Regenerate"
                        >
                          <i className="fas fa-redo"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteReport(report.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="py-8 text-center text-gray-500">
                    <i className="fas fa-file-alt text-3xl mb-3 opacity-50"></i>
                    <p>No reports found</p>
                    <p className="text-sm mt-1">Generate your first report to see it here</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 border-t">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Showing {filteredReports.length} of {reports.length} reports
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-1 border rounded-lg hover:bg-gray-50">
                <i className="fas fa-chevron-left"></i>
              </button>
              <button className="px-3 py-1 border rounded-lg bg-primary-600 text-white">
                1
              </button>
              <button className="px-3 py-1 border rounded-lg hover:bg-gray-50">
                2
              </button>
              <button className="px-3 py-1 border rounded-lg hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-1 border rounded-lg hover:bg-gray-50">
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Storage Management */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Storage Management</h3>
          <p className="text-sm text-gray-500">Manage stored report files</p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h4 className="font-medium">Report Storage Usage</h4>
              <p className="text-sm text-gray-500">Total used: {stats.totalSize}</p>
            </div>
            <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-purple-600"
                style={{ width: '65%' }}
              ></div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => toast.info('Cleaning up old reports...')}
              className="p-4 border rounded-lg text-center hover:bg-gray-50"
            >
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-broom text-yellow-600 text-xl"></i>
              </div>
              <h4 className="font-medium">Cleanup Old Reports</h4>
              <p className="text-sm text-gray-500">Delete reports older than 30 days</p>
            </button>
            
            <button
              onClick={() => toast.info('Exporting report history...')}
              className="p-4 border rounded-lg text-center hover:bg-gray-50"
            >
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-file-export text-green-600 text-xl"></i>
              </div>
              <h4 className="font-medium">Export History</h4>
              <p className="text-sm text-gray-500">Export report history as CSV</p>
            </button>
            
            <button
              onClick={() => toast.info('Backing up report data...')}
              className="p-4 border rounded-lg text-center hover:bg-gray-50"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-cloud-upload-alt text-purple-600 text-xl"></i>
              </div>
              <h4 className="font-medium">Backup Reports</h4>
              <p className="text-sm text-gray-500">Backup to cloud storage</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportHistory;